import { IIssueApi, IStoredIssueColConfigs, ITableTemplate } from "@schemas/issueSchema";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"; // Needed for half-year logic
import quarterOfYear from "dayjs/plugin/quarterOfYear"; // Needed for quarter calculations
import { openapi } from "../../../../openapi";
import { clearAgGridState, setAgGridState } from "../../../global/agGridStateSlice";
import {
  clearAgGridPartialStateFilterModelInLocalStorage,
  removeAgGridPartialStateFromLocalStorage,
  writeAgGridPartialStateToLocalStorage,
} from "../../../hooks/useAgGridReduxState";
import { AppThunk } from "../../../store";
import { getIssueTableGridStateKey, ISSUE_FILTERS_LS_KEY } from "./issueConstants";
import { selectKanbanBoardColumns } from "./issueSelectors";
import {
  clearAllDropdownFilterInRedux,
  setIssueActiveTableTemplateIdInRedux,
  setIssueDropdownFilterAllInRedux,
  setIssueSortedColInRedux,
  setSvarGantEndDateInRedux,
  setSvarGantStartDateInRedux,
  updateColumnStateThunk,
} from "./issueSlice";
import { convertTableTemplateFilterToIssueDropdownFilters } from "./issueUtils";
import { IKanbanCard, IKanbanFrom, IKanbanTo } from "./types";

export const thunkOnKanbanCardDrop =
  (card: IKanbanCard<IIssueApi>, from: IKanbanFrom, to: IKanbanTo): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const kanbanBoardColumns = selectKanbanBoardColumns(state);

    const fromCol = kanbanBoardColumns.find((col) => col.id === from.fromColumnId);
    const toCol = kanbanBoardColumns.find((col) => col.id === to.toColumnId);
    if (!fromCol || !toCol) return;

    const projectId = state.globalReducer.currentProjectId;
    const issueBoardId = state.issueReducerNew.currentIssueBoardId;
    const groupingView = state.issueReducerNew.kanbanGroupingView; // "Status" | "Buckets" | "Tidsfrist"

    dispatch(
      openapi.endpoints.putProjectByProjectIdIssuesKanbanreorder.initiate({
        projectId,
        body: {
          issueBoardId,
          issueId: card?.content?._id,
          boardId: issueBoardId, // samma här
          attributeChanged: groupingView.toLowerCase() as "status" | "bucket" | "tidsfrist",
          sourceColumn: fromCol.value,
          targetColumn: toCol.value,
          targetPosition: to.toPosition,
        },
      }),
    );
  };

// Interval options in Norwegian
export enum GanttInterval {
  ThisYear = "Dette året",
  ThisQuarter = "Dette kvartalet",
  NextThreeMonths = "Neste 3 måneder",
}

dayjs.extend(isSameOrAfter);
dayjs.extend(quarterOfYear);

// Assuming these are your existing helper functions
const getInitStartDate = () => dayjs().startOf("month").toISOString(); // Beginning of current month
const getInitEndDate = () => dayjs().add(1, "month").endOf("month").toISOString(); // End of next month

// Thunk to set Gantt date range based on interval
export const thunkSetSvarGantDateRange = (interval: GanttInterval): AppThunk => {
  return (dispatch) => {
    const today = dayjs();
    let startDate: string;
    let endDate: string;

    switch (interval) {
      case GanttInterval.ThisYear:
        startDate = today.startOf("year").toISOString();
        endDate = today.endOf("year").toISOString();
        break;
      case GanttInterval.ThisQuarter:
        startDate = today.startOf("quarter").toISOString();
        endDate = today.endOf("quarter").toISOString();
        break;
      case GanttInterval.NextThreeMonths:
        startDate = today.startOf("month").toISOString();
        endDate = today.add(3, "month").endOf("month").toISOString();
        break;
      default:
        startDate = getInitStartDate();
        endDate = getInitEndDate();
    }

    dispatch(setSvarGantStartDateInRedux(startDate));
    dispatch(setSvarGantEndDateInRedux(endDate));
  };
};

export const clearAllIssueFilterThunk = (): AppThunk => async (dispatch, getState) => {
  localStorage.removeItem(ISSUE_FILTERS_LS_KEY);
  dispatch(clearAllDropdownFilterInRedux());
};

export const clearIssueTableColumnFiltersThunk = (): AppThunk => {
  return (dispatch, getState) => {
    const issueBoardId = getState().issueReducerNew.currentIssueBoardId;
    if (issueBoardId == null) {
      return;
    }

    const gridStateKey = getIssueTableGridStateKey(issueBoardId);
    clearAgGridPartialStateFilterModelInLocalStorage(gridStateKey);
    dispatch(setAgGridState({ key: gridStateKey, data: { filterModel: {} } }));
  };
};

export const loadIssueTableTemplateThunk = (tableTemplate: ITableTemplate | null): AppThunk => {
  return (dispatch, getState) => {
    const issueBoardId = getState().issueReducerNew.currentIssueBoardId;

    if (tableTemplate != null) {
      const { tableColConfigs, tableColumns = [] } = tableTemplate;
      const columnsToSave: IStoredIssueColConfigs[] = tableColConfigs?.length
        ? tableColConfigs
        : tableColumns.filter((col): col is string => col != null).map((col) => ({ colId: col })); // Fallback to legacy tableColumns if tableColConfigs is not defined
      if (issueBoardId != null) {
        const gridStateKey = getIssueTableGridStateKey(issueBoardId);
        writeAgGridPartialStateToLocalStorage(gridStateKey, {
          columns: columnsToSave
            .filter((columnConfig) => columnConfig.colId != null)
            .map((columnConfig) => ({
              colId: columnConfig.colId,
              width: columnConfig.width,
            })),
          filterModel: {},
        });
        dispatch(setAgGridState({ key: gridStateKey, data: { columnState: null, filterModel: {}, quickFilter: "" } }));
      }
      dispatch(setIssueActiveTableTemplateIdInRedux(tableTemplate._id));
      dispatch(setIssueSortedColInRedux(tableTemplate.sortedCol));
      dispatch(updateColumnStateThunk(columnsToSave));
      dispatch(
        setIssueDropdownFilterAllInRedux({
          filters: convertTableTemplateFilterToIssueDropdownFilters(tableTemplate.filters),
        }),
      );
    }
    if (tableTemplate == null) {
      if (issueBoardId != null) {
        const gridStateKey = getIssueTableGridStateKey(issueBoardId);
        removeAgGridPartialStateFromLocalStorage(gridStateKey);
        dispatch(clearAgGridState({ key: gridStateKey }));
      }
      dispatch(setIssueActiveTableTemplateIdInRedux(null));
      dispatch(updateColumnStateThunk(null));
      dispatch(clearAllIssueFilterThunk());
      dispatch(setIssueSortedColInRedux({ column: "id", direction: "DESC" }));
    }
  };
};

export const resetIssueBoardThunk = (): AppThunk => {
  return (dispatch) => {
    dispatch(setIssueSortedColInRedux({ column: "id", direction: "DESC" }));
    dispatch(clearAllIssueFilterThunk());
    dispatch(setIssueActiveTableTemplateIdInRedux(null));
  };
};
